/**
 * One desktop window: traffic lights, the title (icon, title — top folder, ⓘ,
 * pin star), an editor strip while a record is open, and the screen itself — `ScreenView`, so every open window is
 * a LIVE screen that keeps its state while another is in front.
 *
 * Moving and resizing are direct DOM updates while the pointer is down,
 * committed to the layout's state on release — the screen inside is never
 * re-rendered per pointer move. Dragging the title bar to an edge offers a
 * tile (the layout draws the preview), as macOS does; the green button opens
 * full screen on click and the tiling menu on hover, right-click, ArrowDown or
 * Alt+Enter; double-clicking the title bar fills the desktop (and back).
 */
import { Maximize2, Minus, Pencil, Plus, X } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent as RPointerEvent, type ReactNode } from 'react'
import { tw } from '../../../testHooks'
import { editorTitle } from '../../nav/parts/EditorsList'
import { InfoTip, PinButton } from '../../nav/parts/ScreenTitle'
import { NavIcon } from '../../nav/navIcons'
import { ScreenView } from '../../nav/ShellFrame'
import { S } from '../../nav/tokens'
import type { RenderObjectContext, RunNavItem } from '../../nav/types'
import type { EditorSession, ShellNavModel } from '../../nav/useShellNav'
import { LIGHTS, menuPanel } from './styles'
import {
  MIN_H, MIN_W, TILE_LABELS, clampRect, edgeTarget, type Rect, type TileKind, type WinState,
} from './windows'

export interface MacWindowProps {
  model: ShellNavModel
  item: RunNavItem
  state: WinState
  area: Rect
  isCurrent: boolean
  /** The window fills the viewport with no chrome (shell full screen). */
  full: boolean
  hidden: boolean
  accent: string
  compact: boolean
  shownEditor: EditorSession | null
  renderObject: (item: RunNavItem, ctx?: RenderObjectContext) => ReactNode
  headerExtras?: (item: RunNavItem) => ReactNode
  /** The desktop element (coordinates for edge tiling). */
  desktopEl: () => HTMLElement | null
  onFocus: () => void
  onCommit: (next: WinState) => void
  onTile: (kind: TileKind) => void
  onRestore: () => void
  onArrange: () => void
  onMinimise: () => void
  /** Option/Alt-click on the red button: close every window. */
  onCloseAll: () => void
  onPreview: (kind: TileKind | null) => void
}

type Drag = {
  kind: 'move' | 'resize'
  dir: string
  sx: number
  sy: number
  start: Rect
  rect: Rect
  untiled: boolean
  target: TileKind | null
}

const HANDLES: { dir: string; style: CSSProperties }[] = [
  { dir: 'n', style: { top: -4, left: 10, right: 10, height: 8, cursor: 'ns-resize' } },
  { dir: 's', style: { bottom: -4, left: 10, right: 10, height: 8, cursor: 'ns-resize' } },
  { dir: 'w', style: { left: -4, top: 10, bottom: 10, width: 8, cursor: 'ew-resize' } },
  { dir: 'e', style: { right: -4, top: 10, bottom: 10, width: 8, cursor: 'ew-resize' } },
  { dir: 'nw', style: { top: -5, left: -5, width: 14, height: 14, cursor: 'nwse-resize' } },
  { dir: 'ne', style: { top: -5, right: -5, width: 14, height: 14, cursor: 'nesw-resize' } },
  { dir: 'sw', style: { bottom: -5, left: -5, width: 14, height: 14, cursor: 'nesw-resize' } },
  { dir: 'se', style: { bottom: -5, right: -5, width: 14, height: 14, cursor: 'nwse-resize' } },
]

export function MacWindow(props: MacWindowProps) {
  const {
    model, item, state, area, isCurrent, full, hidden, accent, compact, shownEditor, renderObject,
    headerExtras, desktopEl, onFocus, onCommit, onTile, onRestore, onArrange, onMinimise, onCloseAll, onPreview,
  } = props
  const ref = useRef<HTMLElement>(null)
  const drag = useRef<Drag | null>(null)
  const [tileMenu, setTileMenu] = useState(false)
  const greenRef = useRef<HTMLButtonElement>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const titleHeight = compact ? 38 : 42

  const apply = (r: Rect) => {
    const el = ref.current
    if (!el) return
    el.style.left = `${r.x}px`
    el.style.top = `${r.y}px`
    el.style.width = `${r.w}px`
    el.style.height = `${r.h}px`
  }

  const begin = (e: RPointerEvent<HTMLElement>, kind: Drag['kind'], dir = '') => {
    if (e.button !== 0 || full) return
    e.preventDefault()
    onFocus()
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    drag.current = { kind, dir, sx: e.clientX, sy: e.clientY, start: { ...state.rect }, rect: { ...state.rect }, untiled: false, target: null }
    document.body.style.userSelect = 'none'
  }
  const moveTo = (e: RPointerEvent<HTMLElement>) => {
    const d = drag.current
    if (!d) return
    const dx = e.clientX - d.sx
    const dy = e.clientY - d.sy
    if (d.kind === 'move') {
      let start = d.start
      // A tiled window dragged away takes back its own size, under the pointer.
      if (state.tile && !d.untiled && Math.hypot(dx, dy) > 6) {
        d.untiled = true
        const back = state.restore || { ...start, w: Math.round(start.w * 0.7), h: Math.round(start.h * 0.75) }
        const frac = (d.sx - (desktopEl()?.getBoundingClientRect().left || 0) - start.x) / Math.max(1, start.w)
        start = { x: start.x + start.w * frac - back.w * frac, y: start.y, w: back.w, h: back.h }
        d.start = start
      }
      d.rect = { ...start, x: start.x + dx, y: Math.max(area.y, start.y + dy) }
      const box = desktopEl()?.getBoundingClientRect()
      const t = box ? edgeTarget(e.clientX - box.left, e.clientY - box.top, area) : null
      if (t !== d.target) { d.target = t; onPreview(t) }
    } else {
      const r = { ...d.start }
      if (d.dir.includes('e')) r.w = Math.max(MIN_W, d.start.w + dx)
      if (d.dir.includes('s')) r.h = Math.max(MIN_H, d.start.h + dy)
      if (d.dir.includes('w')) { const w = Math.max(MIN_W, d.start.w - dx); r.x = d.start.x + d.start.w - w; r.w = w }
      if (d.dir.includes('n')) {
        const h = Math.max(MIN_H, d.start.h - dy)
        r.y = Math.max(area.y, d.start.y + d.start.h - h); r.h = d.start.y + d.start.h - r.y
      }
      d.rect = r
    }
    apply(d.rect)
  }
  const end = () => {
    const d = drag.current
    if (!d) return
    drag.current = null
    document.body.style.userSelect = ''
    onPreview(null)
    const moved = d.rect.x !== state.rect.x || d.rect.y !== state.rect.y || d.rect.w !== state.rect.w || d.rect.h !== state.rect.h
    if (d.kind === 'move' && d.target) { onTile(d.target); return }
    if (!moved) { apply(state.rect); return }
    onCommit({ ...state, rect: clampRect(d.rect, area), tile: null, restore: null })
  }
  // A commit (or a reflow) that lands the same numbers React already has would
  // leave a stale drag position on the element: keep it in step.
  // (Never in full screen: there the window is the whole viewport.)
  useLayoutEffect(() => { if (!drag.current && !full) apply(state.rect) })

  useEffect(() => () => { if (hoverTimer.current) clearTimeout(hoverTimer.current) }, [])

  const root = model.pathOf(item)[0]
  const editorHere = shownEditor && shownEditor.objName === item.obj_name ? shownEditor : null
  const minimiseLabel = editorHere && isCurrent ? 'Minimise editor' : 'Minimise window'

  const sectionStyle: CSSProperties = full ? {
    position: 'absolute', inset: 0, zIndex: 30, background: S.bg,
  } : {
    position: 'absolute', left: state.rect.x, top: state.rect.y, width: state.rect.w, height: state.rect.h,
    zIndex: 10 + state.z, borderRadius: 11, overflow: 'visible', background: S.panel,
    border: `1px solid ${isCurrent ? S.borderStrong : S.border}`,
    boxShadow: isCurrent
      ? `0 22px 56px ${S.shadow}, 0 2px 8px rgba(0,0,0,0.14)`
      : `0 10px 28px ${S.shadow}`,
  }

  return (
    <section
      ref={ref}
      aria-label={item.title}
      {...tw('action', 'shell-mac-window', { obj: item.obj_name, current: isCurrent, tile: state.tile || undefined })}
      onPointerDownCapture={() => { if (!isCurrent) onFocus() }}
      style={{ display: hidden ? 'none' : 'flex', flexDirection: 'column', minHeight: 0, ...sectionStyle }}
    >
      {!full && (
        <div
          {...(isCurrent ? tw('screen', `shell-title:${item.obj_name}`) : {})}
          onPointerDown={(e) => {
            if ((e.target as HTMLElement).closest('button, a, input')) return
            begin(e, 'move')
          }}
          onPointerMove={moveTo}
          onPointerUp={end}
          onPointerCancel={end}
          onDoubleClick={(e) => {
            if ((e.target as HTMLElement).closest('button, a')) return
            if (state.tile === 'fill') onRestore(); else onTile('fill')
          }}
          style={{
            flexShrink: 0, height: titleHeight, boxSizing: 'border-box', padding: '0 10px 0 12px',
            display: 'grid', gridTemplateColumns: 'minmax(max-content, 1fr) minmax(0, max-content) minmax(max-content, 1fr)',
            alignItems: 'center', gap: 10, borderBottom: `1px solid ${S.border}`, userSelect: 'none',
            background: isCurrent ? S.surface2 : `color-mix(in srgb, ${S.surface2} 60%, ${S.panel})`,
            borderRadius: '11px 11px 0 0', cursor: 'default', touchAction: 'none',
          }}
        >
          <div className="tw-mac-lights" role="group" aria-label="Window controls" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Light color={LIGHTS.close} label={`Close ${item.title} — Alt/Option-click closes all windows`} hook="shell-mac-close" dim={!isCurrent}
              onClick={(e) => { if (e.altKey) onCloseAll(); else model.closeScreen(item) }} glyph={<X size={9} strokeWidth={3.2} />} />
            <Light color={LIGHTS.minimise} label={minimiseLabel} hook="shell-mac-minimise" dim={!isCurrent}
              onClick={onMinimise} glyph={<Minus size={9} strokeWidth={3.2} />} />
            <span style={{ position: 'relative', display: 'inline-flex' }}
              onMouseEnter={() => { hoverTimer.current = setTimeout(() => setTileMenu(true), 450) }}
              onMouseLeave={() => { if (hoverTimer.current) clearTimeout(hoverTimer.current) }}>
              <Light btnRef={greenRef} color={LIGHTS.zoom} hook="shell-mac-fullscreen" dim={!isCurrent}
                label="Enter full screen — point at it, right-click or press Down for tiling"
                haspopup
                onClick={() => { setTileMenu(false); onFocus(); model.setFullScreen(true) }}
                onContextMenu={(e) => { e.preventDefault(); setTileMenu(true) }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || (e.key === 'Enter' && e.altKey)) { e.preventDefault(); setTileMenu(true) }
                }}
                glyph={<Maximize2 size={8} strokeWidth={3} />} />
              {tileMenu && (
                <TileMenu tiled={!!state.tile} onPick={(k) => { setTileMenu(false); onTile(k) }}
                  onRestore={() => { setTileMenu(false); onRestore() }}
                  onArrange={() => { setTileMenu(false); onArrange() }}
                  onFull={() => { setTileMenu(false); onFocus(); model.setFullScreen(true) }}
                  onClose={(refocus) => { setTileMenu(false); if (refocus) greenRef.current?.focus() }} />
              )}
            </span>
          </div>
          <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
            <span aria-hidden style={{ display: 'inline-flex', color: accent, flexShrink: 0 }}>
              <NavIcon node={item} size={16} />
            </span>
            <h1 className="tw-sh-clip" style={{ margin: 0, fontSize: compact ? 14 : 14.5, fontWeight: 600, color: isCurrent ? S.text : S.text2, minWidth: 0 }}>
              {item.title}
            </h1>
            {root && (
              <span className="tw-sh-clip" style={{ fontSize: 13.5, color: S.text2, minWidth: 0, flexShrink: 1 }}>— {root.title}</span>
            )}
            <InfoTip item={item} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
            {headerExtras?.(item)}
            <PinButton pinned={model.isPinned(item)} onToggle={() => model.togglePin(item)} compact />
          </div>
        </div>
      )}
      {/* No path bar: the title bar already names the screen and its top
          folder. Only an open record editor gets a strip of its own. */}
      {!full && editorHere && (
        <div style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, minHeight: compact ? 28 : 32,
          padding: '0 12px', background: S.panel, borderBottom: `1px solid ${S.border}`, overflow: 'hidden',
        }}>
          <span className="tw-sh-clip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: S.text, fontWeight: 600, minWidth: 0 }}>
            {editorHere.mode === 'add' ? <Plus size={13} aria-hidden /> : <Pencil size={12} aria-hidden />}
            <span className="tw-sh-clip">{editorHere.mode === 'add' ? 'New record' : 'Editing'} · {editorTitle(editorHere)}</span>
          </span>
        </div>
      )}
      <div
        data-tw-open={item.obj_name}
        data-tw-open-type={item.obj_type}
        {...(isCurrent ? { 'data-tw-open-current': '' } : {})}
        style={{
          flex: 1, minHeight: 0, overflow: 'auto', background: S.bg,
          padding: full ? '0.5rem 0.75rem' : compact ? '0.4rem' : '0.4rem 0.75rem 0.75rem',
          borderRadius: full ? 0 : '0 0 11px 11px',
        }}
      >
        <ScreenView model={model} item={item} renderObject={renderObject} background={!isCurrent} />
      </div>
      {!full && HANDLES.map((h) => (
        // A tiled window resizes too (it becomes free at its new size).
        <div key={h.dir} aria-hidden data-tw-mac-resize={h.dir}
          onPointerDown={(e) => begin(e, 'resize', h.dir)} onPointerMove={moveTo} onPointerUp={end} onPointerCancel={end}
          style={{ position: 'absolute', zIndex: 2, touchAction: 'none', ...h.style }} />
      ))}
    </section>
  )
}

/** One traffic light: a real button, its glyph shown on hover / focus. */
function Light({ color, label, hook, onClick, glyph, dim = false, btnRef, haspopup, onContextMenu, onKeyDown }: {
  color: string; label: string; hook: string; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void; glyph: ReactNode; dim?: boolean
  btnRef?: React.RefObject<HTMLButtonElement | null>; haspopup?: boolean
  onContextMenu?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement>) => void
}) {
  return (
    <button ref={btnRef as React.RefObject<HTMLButtonElement>} type="button" className="tw-mac-light" onClick={onClick}
      aria-label={label} title={label} aria-haspopup={haspopup ? 'menu' : undefined}
      onContextMenu={onContextMenu} onKeyDown={onKeyDown}
      {...tw('action', hook)}
      style={{
        width: 24, height: 24, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
      <span aria-hidden style={{
        width: 13, height: 13, borderRadius: '50%', boxShadow: `inset 0 0 0 0.5px ${LIGHTS.ring}`,
        // A window behind the front one shows grey lights, as on the desktop.
        background: dim ? S.borderStrong : color,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: LIGHTS.glyph,
      }}>
        <span className="tw-mac-glyph" style={{ display: 'inline-flex' }}>{glyph}</span>
      </span>
    </button>
  )
}

/** A tiny picture of where a tile puts the window. */
function TileGlyph({ kind }: { kind: TileKind | 'arrange' }) {
  const r = (x: number, y: number, w: number, h: number) => <rect x={x} y={y} width={w} height={h} rx={1} fill="currentColor" />
  const shapes: Record<TileKind | 'arrange', ReactNode> = {
    fill: r(2, 2, 20, 12), left: r(2, 2, 9.5, 12), right: r(12.5, 2, 9.5, 12),
    top: r(2, 2, 20, 5.5), bottom: r(2, 8.5, 20, 5.5),
    tl: r(2, 2, 9.5, 5.5), tr: r(12.5, 2, 9.5, 5.5), bl: r(2, 8.5, 9.5, 5.5), br: r(12.5, 8.5, 9.5, 5.5),
    arrange: <>{r(2, 2, 9.5, 5.5)}{r(12.5, 2, 9.5, 5.5)}{r(2, 8.5, 20, 5.5)}</>,
  }
  return (
    <svg aria-hidden width={24} height={16} viewBox="0 0 24 16" style={{ flexShrink: 0 }}>
      <rect x={0.5} y={0.5} width={23} height={15} rx={2.5} fill="none" stroke="currentColor" strokeOpacity={0.45} />
      <g opacity={0.85}>{shapes[kind]}</g>
    </svg>
  )
}

export const TILE_ORDER: TileKind[] = ['left', 'right', 'top', 'bottom', 'tl', 'tr', 'bl', 'br']

/** The green button's menu (macOS "Move & Resize" / "Fill & Arrange"). */
function TileMenu({ tiled, onPick, onRestore, onArrange, onFull, onClose }: {
  tiled: boolean
  onPick: (k: TileKind) => void
  onRestore: () => void
  onArrange: () => void
  onFull: () => void
  onClose: (refocus: boolean) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(false) }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [onClose])
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const els = [...(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || [])]
    const i = els.indexOf(document.activeElement as HTMLElement)
    const go = (j: number) => { e.preventDefault(); els[(j + els.length) % els.length]?.focus() }
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') go(i + 1)
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') go(i - 1)
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(true) }
    else if (e.key === 'Tab') onClose(false)
  }
  const item = (key: string, label: string, run: () => void, glyph: ReactNode, wide = true) => (
    <button key={key} type="button" role="menuitem" className="tw-sh-item" onClick={run} title={label}
      aria-label={label} {...tw('action', `shell-mac-tile:${key}`)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: wide ? '100%' : 'auto', minHeight: 36,
        padding: wide ? '0 10px' : '0 6px', border: 'none', borderRadius: 7, background: 'transparent',
        color: S.text, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, textAlign: 'left',
        justifyContent: wide ? 'flex-start' : 'center',
      }}>
      {glyph}{wide && label}
    </button>
  )
  const head = (t: string) => <div role="presentation" style={{ padding: '6px 10px 2px', fontSize: 13, fontWeight: 600, color: S.text2 }}>{t}</div>
  return (
    <div ref={ref} role="menu" aria-label="Move, resize and tile" onKeyDown={onKey} data-tw-shell-menu=""
      style={{ ...menuPanel, position: 'absolute', top: 'calc(100% + 6px)', left: -40, minWidth: 250, zIndex: 9441 }}>
      {item('fullscreen', 'Enter full screen', onFull, <Maximize2 size={16} aria-hidden />)}
      {head('Move & Resize')}
      <div role="none" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, padding: '0 4px' }}>
        {TILE_ORDER.map((k) => item(k, TILE_LABELS[k], () => onPick(k), <TileGlyph kind={k} />, false))}
      </div>
      {head('Fill & Arrange')}
      {item('fill', 'Fill', () => onPick('fill'), <TileGlyph kind="fill" />)}
      {item('arrange', 'Arrange all windows', onArrange, <TileGlyph kind="arrange" />)}
      {tiled && item('restore', 'Return to previous size', onRestore, <TileGlyph kind="tl" />)}
    </div>
  )
}

export { TileGlyph }
